import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  relative,
  shortDate,
  useStyleSheet,
} from '../invoices/kit';
import { FEEDBACK_CSS } from './feedbackCss';
import { ImpactTag, StatePill, TypeTag } from './Pills';
import AttachedShots from './AttachedShots';
import FeedbackModal from './FeedbackModal';
import { getMyFeedback } from '../../api_handlers/feedback';
import { stateMeta } from './lib/feedbackTypes';
import { screenLabel } from './lib/screens';
import { isUnseen, markSeen, readSeen } from './lib/unread';

/**
 * What I asked for, and what happened to it.
 *
 * This page is the reason anyone submits a second time. It shows the
 * state in the submitter's language rather than the board's -- they do not
 * care which column an item sits in, they care whether anything is going
 * to happen -- and it shows the team's reply in full.
 *
 * Seen state is recorded once the list has rendered, so an item that
 * moved since the last visit carries a dot exactly once.
 */

function Row({ item, unseen }) {
  const meta = stateMeta(item.state);
  return (
    <li className="fb-item" style={{ cursor: 'default' }}>
      <div className="fb-item__top">
        {unseen ? <span className="fb-item__dot" aria-label="Updated since you last looked" /> : null}
        <StatePill state={item.state} />
        <TypeTag type={item.type} />
        <ImpactTag impact={item.impact} />
      </div>
      <p className="fb-item__title">{item.title}</p>
      <p className="fb-item__detail">{item.detail}</p>
      <div className="fb-item__foot">
        <span>{meta.says}</span>
        <span aria-hidden="true">·</span>
        <span>{screenLabel(item.screen)}</span>
        <span aria-hidden="true">·</span>
        <span title={shortDate(item.created_at)}>Raised {relative(item.created_at)}</span>
      </div>
      <AttachedShots shots={item.screenshots} />
      {item.reply ? (
        <div className="fb-reply">
          <p className="fb-reply__who">From the product team</p>
          <p className="fb-reply__text">{item.reply}</p>
        </div>
      ) : null}
    </li>
  );
}

function Skeleton() {
  return (
    <ul className="fb-list">
      {[0, 1, 2].map((n) => (
        <li key={n} className="fb-item" style={{ cursor: 'default' }}>
          <div className="iv-sk" style={{ height: 18, width: 120, marginBottom: 10 }} />
          <div className="iv-sk" style={{ height: 15, width: '70%', marginBottom: 8 }} />
          <div className="iv-sk" style={{ height: 13, width: '92%' }} />
        </li>
      ))}
    </ul>
  );
}

export default function MyFeedback() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('fb-kit', FEEDBACK_CSS);

  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(() => readSeen());
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [raising, setRaising] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailure('');
    return getMyFeedback()
      .then((rows) => {
        setItems(rows);
        // Read the stamps before overwriting them, so this render still
        // shows which rows moved.
        setSeen(readSeen());
        markSeen(null, rows);
      })
      .catch((error) => setFailure(error.message || 'We could not load your requests.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="iv-root iv-page">
      <div className="iv-wrap iv-wrap--reading">
        <div className="iv-head">
          <div>
            <h1 className="iv-h1">My requests</h1>
            <p className="iv-lede">
              Everything you have raised about the dashboard, and where each one got to. Replies
              from the product team show up here.
            </p>
          </div>
          <button type="button" className="iv-btn iv-btn--primary" onClick={() => setRaising(true)}>
            <Icon path={ICONS.plus} size={15} />
            Raise something
          </button>
        </div>

        {failure ? <Alert onRetry={load}>{failure}</Alert> : null}

        {loading ? <Skeleton /> : null}

        {!loading && !failure && items.length === 0 ? (
          <Empty icon={ICONS.pencil} title="Nothing raised yet">
            When something in the dashboard slows you down or is missing, tell us with the
            Feedback button in the header. You will be able to follow it from here.
          </Empty>
        ) : null}

        {!loading && items.length > 0 ? (
          <ul className="fb-list">
            {items.map((item) => (
              <Row key={item.id} item={item} unseen={isUnseen(item, seen)} />
            ))}
          </ul>
        ) : null}
      </div>

      {raising ? (
        <FeedbackModal
          open
          onClose={() => setRaising(false)}
          onSubmitted={() => load()}
        />
      ) : null}
    </div>
  );
}
